import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import LanguageToggle from '../components/game/LanguageToggle';
import Sidebar from '../components/game/Sidebar';
import { motion } from 'framer-motion';
import { Users, DollarSign, Gamepad2, BookOpen, Trophy, TrendingUp, School, Activity, Download, UserPlus, Repeat, Target, Zap, Trash2, Mail } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, AreaChart, Area, FunnelChart } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PLAN_COLORS = { Free: '#9ca3af', Basic: '#2196F3', Pro: '#E85D3A', School: '#009688' };

export default function AdminPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [saas, setSaas] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('saas');
  const [teachers, setTeachers] = useState({ teachers: [], count: 0, max: 10 });
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [teacherToast, setTeacherToast] = useState('');
  const isEN = language === 'en';
  const headers = { Authorization: `Bearer ${token}` };
  const headersJson = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const planColorMap = { free: '#9ca3af', basic: '#2196F3', pro: '#E85D3A', school: '#009688' };

  useEffect(() => { if (!token) { navigate('/login'); return; } fetchAll(); }, [token]);

  const fetchAll = async () => {
    try {
      const [sRes, uRes, aRes, anRes, saasRes, actRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/stats`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/users?limit=100`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/affiliates`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/analytics`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/saas-metrics`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/activity`, { headers }),
      ]);
      if (sRes.ok) setStats(await sRes.json());
      if (uRes.ok) setUsers(await uRes.json());
      if (aRes.ok) setAffiliates(await aRes.json());
      if (anRes.ok) setAnalytics(await anRes.json());
      if (saasRes.ok) setSaas(await saasRes.json());
      if (actRes.ok) setActivity(await actRes.json());
      // Fetch teachers
      const tRes = await fetch(`${BACKEND_URL}/api/school/teachers`, { headers });
      if (tRes.ok) setTeachers(await tRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const inviteTeacher = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/school/invite`, {
        method: 'POST', headers: headersJson,
        body: JSON.stringify({ email: inviteEmail.trim(), name: inviteName.trim() })
      });
      if (res.ok) {
        setTeacherToast(isEN ? 'Teacher invited!' : 'Guru dijemput!');
        setInviteEmail(''); setInviteName('');
        const tRes = await fetch(`${BACKEND_URL}/api/school/teachers`, { headers });
        if (tRes.ok) setTeachers(await tRes.json());
      } else {
        const e = await res.json();
        setTeacherToast(e.detail || 'Error');
      }
    } catch { setTeacherToast('Error'); }
    setInviting(false);
    setTimeout(() => setTeacherToast(''), 3000);
  };

  const removeTeacher = async (email) => {
    if (!window.confirm(isEN ? `Remove ${email}?` : `Buang ${email}?`)) return;
    await fetch(`${BACKEND_URL}/api/school/teachers/${email}`, { method: 'DELETE', headers });
    const tRes = await fetch(`${BACKEND_URL}/api/school/teachers`, { headers });
    if (tRes.ok) setTeachers(await tRes.json());
  };

  const updatePlan = async (email, plan) => {
    await fetch(`${BACKEND_URL}/api/admin/users/${email}/plan?plan=${plan}`, { method: 'PUT', headers });
    fetchAll();
  };

  const exportCSV = async () => {
    const res = await fetch(`${BACKEND_URL}/api/admin/export/users`, { headers });
    const data = await res.json();
    const csv = "Name,Email,Plan,School,Created\n" + data.map(u => `"${u.name}","${u.email}","${u.plan}","${u.school_name || ''}","${u.created_at || ''}"`).join("\n");
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'pantass_users.csv'; a.click();
  };

  const formatTime = (t) => { if (!t) return ''; const d = new Date(t); const mins = Math.floor((Date.now() - d) / 60000); if (mins < 60) return `${mins}m ago`; if (mins < 1440) return `${Math.floor(mins/60)}h ago`; return `${Math.floor(mins/1440)}d ago`; };

  return (
    <div className="dashboard-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <header className="dashboard-header">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)} aria-label="Menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
        </button>
        <h1 className="dashboard-header-title">Admin Dashboard</h1>
        <LanguageToggle className="dashboard-lang-toggle" />
      </header>

      <div className="dashboard-content">
        {loading ? <div className="lb-loading"><div className="lb-spinner" /></div> : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="admin-tabs-scroll">
              <div className="lb-tabs" style={{ marginBottom: '20px' }}>
                {['saas','analytics','users','payments','affiliates','activity','teachers','ai-team'].map(t => (
                  <button key={t} className={`lb-tab ${tab === t ? 'lb-tab-active' : ''}`} onClick={() => setTab(t)}>
                    {t === 'saas' ? 'SaaS Metrics' : t === 'analytics' ? (isEN ? 'Analytics' : 'Analitik') : t === 'users' ? (isEN ? 'Users' : 'Pengguna') : t === 'payments' ? (isEN ? 'Payments' : 'Pembayaran') : t === 'affiliates' ? 'Affiliates' : t === 'teachers' ? (isEN ? 'Teachers' : 'Guru') : t === 'ai-team' ? (isEN ? 'AI Team' : 'Pasukan AI') : (isEN ? 'Activity' : 'Aktiviti')}
                  </button>
                ))}
              </div>
            </div>

            {/* ===== SAAS METRICS ===== */}
            {tab === 'saas' && saas && (
              <>
                {/* KPI Cards Row 1 */}
                <div className="stats-grid">
                  <div className="stat-card stat-card-green"><DollarSign size={22} /><div className="stat-card-value">RM{saas.mrr}</div><div className="stat-card-label">MRR</div></div>
                  <div className="stat-card stat-card-blue"><TrendingUp size={22} /><div className="stat-card-value">RM{saas.arr}</div><div className="stat-card-label">ARR</div></div>
                  <div className="stat-card stat-card-orange"><Target size={22} /><div className="stat-card-value">{saas.conversion_rate}%</div><div className="stat-card-label">{isEN ? 'Conversion Rate' : 'Kadar Penukaran'}</div></div>
                  <div className="stat-card stat-card-purple"><Zap size={22} /><div className="stat-card-value">RM{saas.ltv}</div><div className="stat-card-label">LTV</div></div>
                </div>

                {/* KPI Cards Row 2 */}
                <div className="stats-grid" style={{ marginTop: '12px' }}>
                  <div className="stat-card"><Users size={18} style={{ color: '#666' }} /><div className="stat-card-value">{saas.total_users}</div><div className="stat-card-label">{isEN ? 'Total Users' : 'Jumlah Pengguna'}</div></div>
                  <div className="stat-card"><UserPlus size={18} style={{ color: '#22c55e' }} /><div className="stat-card-value">{saas.paid_users}</div><div className="stat-card-label">{isEN ? 'Paid Users' : 'Pengguna Berbayar'}</div></div>
                  <div className="stat-card"><div className="stat-card-value">RM{saas.arpu}</div><div className="stat-card-label">ARPU</div></div>
                  <div className="stat-card"><div className="stat-card-value">RM{saas.arppu}</div><div className="stat-card-label">ARPPU</div></div>
                </div>

                {/* MRR Trend + Plan Donut */}
                <div className="admin-charts-row" style={{ marginTop: '20px' }}>
                  <div className="admin-chart-card">
                    <h3>{isEN ? 'MRR Trend' : 'Trend MRR'}</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart data={saas.mrr_trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip formatter={(v) => `RM${v}`} />
                        <Area type="monotone" dataKey="mrr" stroke="#22c55e" fill="#22c55e" fillOpacity={0.15} strokeWidth={3} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="admin-chart-card">
                    <h3>{isEN ? 'Plan Distribution' : 'Taburan Pelan'}</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={saas.plan_breakdown} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value" label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}>
                          {saas.plan_breakdown.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Conversion Funnel + Active Users */}
                <div className="admin-charts-row" style={{ marginTop: '16px' }}>
                  <div className="admin-chart-card">
                    <h3>{isEN ? 'Conversion Funnel' : 'Corong Penukaran'}</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={saas.funnel} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis type="number" />
                        <YAxis dataKey="stage" type="category" width={80} tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                          {saas.funnel.map((_, i) => <Cell key={i} fill={['#9ca3af', '#2196F3', '#ff8c1a', '#22c55e'][i]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="admin-chart-card">
                    <h3>{isEN ? 'Active Usage' : 'Penggunaan Aktif'}</h3>
                    <div className="saas-active-grid">
                      <div className="saas-active-item"><div className="saas-active-num">{saas.dau}</div><div className="saas-active-label">{isEN ? 'Games Today' : 'Permainan Hari Ini'}</div></div>
                      <div className="saas-active-item"><div className="saas-active-num">{saas.wau}</div><div className="saas-active-label">{isEN ? 'Games This Week' : 'Permainan Minggu Ini'}</div></div>
                      <div className="saas-active-item"><div className="saas-active-num">{saas.mau}</div><div className="saas-active-label">{isEN ? 'Games This Month' : 'Permainan Bulan Ini'}</div></div>
                      <div className="saas-active-item saas-active-highlight"><div className="saas-active-num">{saas.new_today}</div><div className="saas-active-label">{isEN ? 'New Users Today' : 'Pengguna Baru Hari Ini'}</div></div>
                      <div className="saas-active-item"><div className="saas-active-num">{saas.new_week}</div><div className="saas-active-label">{isEN ? 'New This Week' : 'Baru Minggu Ini'}</div></div>
                      <div className="saas-active-item"><div className="saas-active-num">{saas.new_month}</div><div className="saas-active-label">{isEN ? 'New This Month' : 'Baru Bulan Ini'}</div></div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ===== ANALYTICS ===== */}
            {tab === 'analytics' && analytics && (
              <>
                <div className="admin-chart-card admin-chart-full">
                  <h3>{isEN ? 'User Growth (Last 30 Days)' : 'Pertumbuhan Pengguna (30 Hari)'}</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={analytics.user_growth}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis /><Tooltip /><Line type="monotone" dataKey="users" stroke="#1a8cff" strokeWidth={3} dot={{ r: 4 }} /></LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="admin-chart-card admin-chart-full" style={{ marginTop: '16px' }}>
                  <h3>{isEN ? 'Games Played (Last 30 Days)' : 'Permainan (30 Hari)'}</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={analytics.games_per_day}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis /><Tooltip /><Bar dataKey="games" fill="#ff8c1a" radius={[6, 6, 0, 0]} /></BarChart>
                  </ResponsiveContainer>
                </div>
                {analytics.top_schools.length > 0 && (
                  <div className="admin-chart-card admin-chart-full" style={{ marginTop: '16px' }}>
                    <h3>{isEN ? 'Top Schools' : 'Sekolah Teratas'}</h3>
                    <ResponsiveContainer width="100%" height={Math.max(200, analytics.top_schools.length * 40)}>
                      <BarChart data={analytics.top_schools} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis type="number" /><YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 12 }} /><Tooltip /><Bar dataKey="users" fill="#009688" radius={[0, 6, 6, 0]} /></BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            )}

            {/* ===== USERS ===== */}
            {tab === 'users' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', alignItems: 'center' }}>
                  <span style={{ fontWeight: 800, fontSize: '14px', color: '#666' }}>{users.length} {isEN ? 'users' : 'pengguna'}</span>
                  <button onClick={exportCSV} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', border: '1px solid #334155', borderRadius: '8px', background: '#1e293b', color: '#f8fafc', fontWeight: 800, fontSize: '13px', cursor: 'pointer' }}><Download size={14} /> CSV</button>
                </div>
                <div className="lb-table-container"><table className="lb-table"><thead><tr><th>#</th><th>{isEN ? 'Name' : 'Nama'}</th><th>Email</th><th>{isEN ? 'Plan' : 'Pelan'}</th><th>{isEN ? 'School' : 'Sekolah'}</th><th>{isEN ? 'Ref' : 'Rujukan'}</th><th>{isEN ? 'Change' : 'Tukar'}</th></tr></thead><tbody>
                  {users.map((u, i) => (<tr key={i}><td>{i + 1}</td><td className="lb-team-name">{u.name}</td><td style={{ fontSize: '12px' }}>{u.email}</td><td><span style={{ background: planColorMap[u.plan] || '#9ca3af', color: 'white', padding: '2px 10px', borderRadius: '100px', fontSize: '11px', fontWeight: 800 }}>{(u.plan || 'free').toUpperCase()}</span></td><td style={{ fontSize: '12px' }}>{u.school_name || '-'}</td><td style={{ fontSize: '12px' }}>{u.referred_by || '-'}</td><td><select value={u.plan || 'free'} onChange={e => updatePlan(u.email, e.target.value)} style={{ padding: '4px 8px', borderRadius: '8px', border: '1px solid #334155', fontSize: '12px', fontWeight: 700 }}><option value="free">Free</option><option value="basic">Basic</option><option value="pro">Pro</option><option value="school">School</option></select></td></tr>))}
                </tbody></table></div>
              </>
            )}

            {/* ===== PAYMENTS ===== */}
            {tab === 'payments' && analytics && stats && (
              <>
                <div className="admin-charts-row">
                  <div className="admin-chart-card">
                    <h3>{isEN ? 'Revenue Split' : 'Pecahan Pendapatan'}</h3>
                    <ResponsiveContainer width="100%" height={250}><PieChart><Pie data={analytics.revenue_by_plan.filter(r => r.revenue > 0)} cx="50%" cy="50%" innerRadius={60} outerRadius={95} paddingAngle={3} dataKey="revenue" label={({ name, revenue }) => `${name}: RM${revenue}`}>{analytics.revenue_by_plan.map((e, i) => <Cell key={i} fill={PLAN_COLORS[e.name] || '#999'} />)}</Pie><Tooltip formatter={(v) => `RM${v}`} /></PieChart></ResponsiveContainer>
                  </div>
                  <div className="admin-chart-card">
                    <h3>{isEN ? 'Subscribers' : 'Pelanggan'}</h3>
                    <ResponsiveContainer width="100%" height={250}><BarChart data={analytics.revenue_by_plan}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="name" /><YAxis /><Tooltip /><Bar dataKey="users" radius={[8, 8, 0, 0]}>{analytics.revenue_by_plan.map((e, i) => <Cell key={i} fill={PLAN_COLORS[e.name] || '#999'} />)}</Bar></BarChart></ResponsiveContainer>
                  </div>
                </div>
                <h3 style={{ fontFamily: 'Nunito', fontSize: '18px', fontWeight: 900, marginTop: '24px', marginBottom: '12px' }}>{isEN ? 'Recent Upgrades' : 'Naik Taraf Terbaru'}</h3>
                {analytics.recent_payments.length === 0 ? <div className="lb-empty">{isEN ? 'No paid users yet' : 'Tiada pengguna berbayar'}</div> : (
                  <div className="lb-table-container"><table className="lb-table"><thead><tr><th>#</th><th>{isEN ? 'Name' : 'Nama'}</th><th>Email</th><th>{isEN ? 'Plan' : 'Pelan'}</th><th>{isEN ? 'Revenue/mo' : 'Pendapatan/bln'}</th></tr></thead><tbody>
                    {analytics.recent_payments.map((u, i) => { const rev = { basic: 19.90, pro: 49.90, school: 199 }[u.plan] || 0; return (<tr key={i}><td>{i + 1}</td><td className="lb-team-name">{u.name}</td><td style={{ fontSize: '12px' }}>{u.email}</td><td><span style={{ background: planColorMap[u.plan] || '#9ca3af', color: 'white', padding: '2px 10px', borderRadius: '100px', fontSize: '11px', fontWeight: 800 }}>{(u.plan || 'free').toUpperCase()}</span></td><td className="lb-total-score">RM{rev}</td></tr>); })}
                  </tbody></table></div>
                )}
                <div className="admin-payment-summary"><div><strong>MRR:</strong> RM{stats.monthly_revenue}</div><div><strong>ARR:</strong> RM{stats.annual_revenue}</div><div><strong>{isEN ? 'Paying Users:' : 'Pengguna Berbayar:'}</strong> {stats.basic_users + stats.pro_users + stats.school_users}</div></div>
              </>
            )}

            {/* ===== AFFILIATES ===== */}
            {tab === 'affiliates' && (
              affiliates.length === 0 ? <div className="lb-empty">{isEN ? 'No affiliates yet' : 'Tiada affiliate'}</div> : (
              <div className="lb-table-container"><table className="lb-table"><thead><tr><th>#</th><th>{isEN ? 'Name' : 'Nama'}</th><th>Email</th><th>{isEN ? 'Code' : 'Kod'}</th><th>{isEN ? 'Signups' : 'Daftar'}</th><th>{isEN ? 'Paid' : 'Berbayar'}</th><th>{isEN ? 'Earnings' : 'Pendapatan'}</th></tr></thead><tbody>
                {affiliates.map((a, i) => (<tr key={i}><td>{i + 1}</td><td className="lb-team-name">{a.name}</td><td style={{ fontSize: '12px' }}>{a.email}</td><td><code style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: '6px', fontSize: '13px' }}>{a.code}</code></td><td>{a.total_signups}</td><td className="lb-wins">{a.total_paid_referrals}</td><td className="lb-total-score">RM{a.total_earnings}</td></tr>))}
              </tbody></table></div>)
            )}

            {/* ===== ACTIVITY ===== */}
            {tab === 'activity' && (
              <div className="admin-activity-feed">
                {activity.length === 0 ? <div className="lb-empty">{isEN ? 'No recent activity' : 'Tiada aktiviti terbaru'}</div> : activity.map((act, i) => (
                  <div key={i} className="admin-activity-item">
                    <div className={`admin-activity-icon ${act.type === 'signup' ? 'admin-activity-signup' : 'admin-activity-match'}`}>
                      {act.type === 'signup' ? <UserPlus size={16} /> : <Gamepad2 size={16} />}
                    </div>
                    <div className="admin-activity-content">
                      {act.type === 'signup' ? (
                        <span><strong>{act.name}</strong> {isEN ? 'signed up' : 'mendaftar'} <span style={{ background: planColorMap[act.plan] || '#9ca3af', color: 'white', padding: '1px 8px', borderRadius: '100px', fontSize: '11px', fontWeight: 800, marginLeft: '4px' }}>{(act.plan || 'free').toUpperCase()}</span></span>
                      ) : (
                        <span><strong>{act.team1}</strong> vs <strong>{act.team2}</strong> {act.school && <span style={{ color: '#2196F3', fontSize: '12px' }}>({act.school})</span>}</span>
                      )}
                    </div>
                    <div className="admin-activity-time">{formatTime(act.time)}</div>
                  </div>
                ))}
              </div>
            )}

            {/* ===== TEACHERS (School Sub-Accounts) ===== */}
            {tab === 'teachers' && (
              <div className="admin-teachers" data-testid="admin-teachers">
                <div className="admin-teachers-header">
                  <h3>{isEN ? 'School Teacher Accounts' : 'Akaun Guru Sekolah'}</h3>
                  <p>{isEN ? 'Invite up to 10 teachers to use PANTASS with your School plan.' : 'Jemput sehingga 10 guru untuk guna PANTASS dengan pelan Sekolah anda.'}</p>
                  <div className="admin-teachers-counter">
                    <span>{teachers.count}/{teachers.max}</span>
                    <span>{isEN ? 'teachers' : 'guru'}</span>
                  </div>
                </div>

                {/* Toast */}
                {teacherToast && (
                  <div style={{ background: '#dcfce7', color: '#166534', padding: '10px 16px', borderRadius: '12px', fontWeight: 700, marginBottom: '12px', textAlign: 'center' }}>
                    {teacherToast}
                  </div>
                )}

                {/* Invite Form */}
                <div className="admin-invite-form" data-testid="invite-teacher-form">
                  <h4><UserPlus size={16} /> {isEN ? 'Invite Teacher' : 'Jemput Guru'}</h4>
                  <div className="admin-invite-row">
                    <input className="admin-invite-input" type="email" value={inviteEmail}
                      onChange={e => setInviteEmail(e.target.value)} placeholder={isEN ? 'Teacher email' : 'E-mel guru'}
                      data-testid="invite-email-input" />
                    <input className="admin-invite-input" value={inviteName}
                      onChange={e => setInviteName(e.target.value)} placeholder={isEN ? 'Name (optional)' : 'Nama (pilihan)'}
                      data-testid="invite-name-input" />
                    <button className="admin-invite-btn" onClick={inviteTeacher} disabled={inviting || !inviteEmail.trim() || teachers.count >= teachers.max}
                      data-testid="invite-submit-btn">
                      {inviting ? '...' : <><Mail size={16} /> {isEN ? 'Invite' : 'Jemput'}</>}
                    </button>
                  </div>
                  {teachers.count >= teachers.max && (
                    <p className="admin-invite-limit">{isEN ? 'Maximum 10 teachers reached.' : 'Had maksimum 10 guru dicapai.'}</p>
                  )}
                </div>

                {/* Teachers List */}
                {teachers.teachers.length === 0 ? (
                  <div className="lb-empty">{isEN ? 'No teachers invited yet. Invite your first teacher!' : 'Tiada guru dijemput lagi. Jemput guru pertama anda!'}</div>
                ) : (
                  <div className="admin-teachers-list">
                    {teachers.teachers.map((t, i) => (
                      <div key={i} className="admin-teacher-card" data-testid={`teacher-card-${i}`}>
                        <div className="admin-teacher-avatar" style={{ background: '#009688' }}>
                          {(t.teacher_name || '?')[0].toUpperCase()}
                        </div>
                        <div className="admin-teacher-info">
                          <div className="admin-teacher-name">{t.teacher_name || t.teacher_email}</div>
                          <div className="admin-teacher-email">{t.teacher_email}</div>
                          <div className="admin-teacher-meta">
                            <span className="admin-teacher-school">{t.school_name}</span>
                            <span className="admin-teacher-status">{t.status === 'active' ? (isEN ? 'Active' : 'Aktif') : t.status}</span>
                          </div>
                        </div>
                        <button className="admin-teacher-remove" onClick={() => removeTeacher(t.teacher_email)} data-testid={`remove-teacher-${i}`}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* How it works */}
                <div className="admin-teachers-info">
                  <h4>{isEN ? 'How it works' : 'Cara berfungsi'}</h4>
                  <ul>
                    <li>{isEN ? 'Enter teacher\'s email and click Invite' : 'Masukkan e-mel guru dan klik Jemput'}</li>
                    <li>{isEN ? 'If they have a PANTASS account, their plan auto-upgrades to School' : 'Jika mereka ada akaun PANTASS, pelan mereka auto naik taraf ke School'}</li>
                    <li>{isEN ? 'If not, they\'ll get School access when they register with that email' : 'Jika tidak, mereka akan dapat akses School bila daftar dengan e-mel tersebut'}</li>
                    <li>{isEN ? 'Removing a teacher downgrades them back to Free plan' : 'Membuang guru akan turunkan mereka ke pelan Percuma'}</li>
                  </ul>
                </div>
              </div>
            )}

            {/* ===== AI TEAM ORG CHART ===== */}
            {tab === 'ai-team' && (
              <div className="ai-org" data-testid="ai-team-org">
                <div className="ai-org-header">
                  <h3>{isEN ? 'PANTASS AI Team' : 'Pasukan AI PANTASS'}</h3>
                  <p>{isEN ? 'AI-powered operations team' : 'Pasukan operasi dikuasakan AI'}</p>
                </div>

                {/* CEO */}
                <div className="ai-org-ceo">
                  <div className="ai-org-card ai-org-ceo-card">
                    <div className="ai-org-avatar" style={{ background: '#3b82f6' }}>P</div>
                    <div className="ai-org-info">
                      <div className="ai-org-name">PANTASS</div>
                      <div className="ai-org-role">CEO / Founder</div>
                    </div>
                    <div className="ai-org-status ai-org-online" />
                  </div>
                </div>
                <div className="ai-org-line-v" />

                {/* CSO - JARVIS */}
                <div className="ai-org-cso">
                  <div className="ai-org-card ai-org-jarvis">
                    <div className="ai-org-avatar" style={{ background: '#8b5cf6' }}>JA</div>
                    <div className="ai-org-info">
                      <div className="ai-org-name">JARVIS</div>
                      <div className="ai-org-role">{isEN ? 'Chief Strategy Officer' : 'Ketua Pegawai Strategi'}</div>
                      <div className="ai-org-tags">
                        <span className="ai-org-tag">{isEN ? 'STRATEGIC PLANNING' : 'PERANCANGAN STRATEGIK'}</span>
                        <span className="ai-org-tag">{isEN ? 'TASK ORCHESTRATION' : 'ORKESTRASI TUGAS'}</span>
                      </div>
                    </div>
                    <div className="ai-org-status ai-org-online" />
                  </div>
                </div>
                <div className="ai-org-line-v" />
                <div className="ai-org-line-h" />

                {/* Departments */}
                <div className="ai-org-departments">
                  {/* EDUCATION */}
                  <div className="ai-org-dept">
                    <div className="ai-org-dept-label">{isEN ? '📚 EDUCATION' : '📚 PENDIDIKAN'}</div>
                    <div className="ai-org-card">
                      <div className="ai-org-avatar" style={{ background: '#22c55e' }}>QZ</div>
                      <div className="ai-org-info">
                        <div className="ai-org-name">QUIZZY</div>
                        <div className="ai-org-role">{isEN ? 'Question Generator' : 'Penjana Soalan'}</div>
                        <div className="ai-org-tags">
                          <span className="ai-org-tag">AI QUESTIONS</span>
                          <span className="ai-org-tag">KSSR/KSSM</span>
                        </div>
                      </div>
                      <div className="ai-org-status ai-org-online" />
                    </div>
                    <div className="ai-org-card">
                      <div className="ai-org-avatar" style={{ background: '#14b8a6' }}>CR</div>
                      <div className="ai-org-info">
                        <div className="ai-org-name">CURATOR</div>
                        <div className="ai-org-role">{isEN ? 'Content Curator' : 'Kurator Kandungan'}</div>
                        <div className="ai-org-tags">
                          <span className="ai-org-tag">{isEN ? 'SYLLABUS ALIGNMENT' : 'PENJAJARAN SILIBUS'}</span>
                          <span className="ai-org-tag">QC</span>
                        </div>
                      </div>
                      <div className="ai-org-status ai-org-standby" />
                    </div>
                  </div>

                  {/* GROWTH */}
                  <div className="ai-org-dept">
                    <div className="ai-org-dept-label">{isEN ? '📈 GROWTH' : '📈 PERTUMBUHAN'}</div>
                    <div className="ai-org-card">
                      <div className="ai-org-avatar" style={{ background: '#f59e0b' }}>SC</div>
                      <div className="ai-org-info">
                        <div className="ai-org-name">SCRIBE</div>
                        <div className="ai-org-role">{isEN ? 'Content Director' : 'Pengarah Kandungan'}</div>
                        <div className="ai-org-tags">
                          <span className="ai-org-tag">{isEN ? 'COPYWRITING' : 'PENULISAN'}</span>
                          <span className="ai-org-tag">SOCIAL MEDIA</span>
                        </div>
                      </div>
                      <div className="ai-org-status ai-org-standby" />
                    </div>
                    <div className="ai-org-card">
                      <div className="ai-org-avatar" style={{ background: '#ef4444' }}>TR</div>
                      <div className="ai-org-info">
                        <div className="ai-org-name">TRENDY</div>
                        <div className="ai-org-role">{isEN ? 'Viral Scout' : 'Pengakap Viral'}</div>
                        <div className="ai-org-tags">
                          <span className="ai-org-tag">{isEN ? 'TREND DETECTION' : 'PENGESANAN TREND'}</span>
                          <span className="ai-org-tag">TIKTOK</span>
                        </div>
                      </div>
                      <div className="ai-org-status ai-org-standby" />
                    </div>
                  </div>

                  {/* USER SUCCESS */}
                  <div className="ai-org-dept">
                    <div className="ai-org-dept-label">{isEN ? '💚 USER SUCCESS' : '💚 KEJAYAAN PENGGUNA'}</div>
                    <div className="ai-org-card">
                      <div className="ai-org-avatar" style={{ background: '#6366f1' }}>SA</div>
                      <div className="ai-org-info">
                        <div className="ai-org-name">SAGE</div>
                        <div className="ai-org-role">{isEN ? 'User Success Agent' : 'Agen Kejayaan Pengguna'}</div>
                        <div className="ai-org-tags">
                          <span className="ai-org-tag">{isEN ? 'CHURN PREVENTION' : 'CEGAH CHURN'}</span>
                          <span className="ai-org-tag">EMAILS</span>
                        </div>
                      </div>
                      <div className="ai-org-status ai-org-standby" />
                    </div>
                    <div className="ai-org-card">
                      <div className="ai-org-avatar" style={{ background: '#ec4899' }}>NU</div>
                      <div className="ai-org-info">
                        <div className="ai-org-name">NURTURE</div>
                        <div className="ai-org-role">{isEN ? 'Parent Engagement' : 'Penglibatan Ibu Bapa'}</div>
                        <div className="ai-org-tags">
                          <span className="ai-org-tag">{isEN ? 'PROGRESS REPORTS' : 'LAPORAN KEMAJUAN'}</span>
                          <span className="ai-org-tag">WHATSAPP</span>
                        </div>
                      </div>
                      <div className="ai-org-status ai-org-standby" />
                    </div>
                  </div>
                </div>

                {/* Status Legend */}
                <div className="ai-org-legend">
                  <span><span className="ai-org-status ai-org-online" style={{ position: 'relative', display: 'inline-block', marginRight: '6px' }} /> {isEN ? 'Active' : 'Aktif'}</span>
                  <span><span className="ai-org-status ai-org-standby" style={{ position: 'relative', display: 'inline-block', marginRight: '6px' }} /> {isEN ? 'Coming Soon' : 'Akan Datang'}</span>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
